import { useCallback, useState } from 'react';
import { Button } from '@ds';
import { useResourcesHandlers } from '@lp/features/RoutePlanner/hooks/useResourcesHandlers';
import { useHubResourcesList } from '@lp/features/RoutePlanner/hooks/useHubResourcesList';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import { Case } from '@/api/apis/case';
import { OcsConsoleData } from '@/api/apis/resources';
import { RoutePlannerDeviceList } from '../DeviceListTable/DeviceList';

type SelectSecondaryOCSProps = {
  onClose: () => void;
  setParentLoading?: (loading: boolean) => void;
};

export const SelectSecondaryOCS = ({ onClose, setParentLoading }: SelectSecondaryOCSProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { caseId, organType } = useCaseDetailsWithReviews() as Case;
  const { handleResourceAssignment } = useResourcesHandlers({ id: caseId });
  const [selectedSecondaryOCS, setSelectedSecondaryOCS] = useState<OcsConsoleData | null>(null);

  const { selectedHub } = useHubResourcesList({ fetchResources: false });

  const handleReserve = useCallback(
    async (secondaryOCS: OcsConsoleData) => {
      try {
        setIsLoading(true);
        setParentLoading?.(true);
        await handleResourceAssignment({ data: { ocsConsoleIds: [secondaryOCS.id] }, type: 'reserve' });
        onClose();
      } finally {
        setIsLoading(false);
        setParentLoading?.(false);
      }
    },
    [handleResourceAssignment, onClose, setParentLoading],
  );

  const handleOCSSelect = (device: OcsConsoleData) => {
    setSelectedSecondaryOCS(device);
  };

  const handleAddResource = async () => {
    if (selectedSecondaryOCS) {
      await handleReserve(selectedSecondaryOCS);
    }
  };

  return (
    <div className="flex flex-col place-items-stretch gap-6">
      <div className="flex flex-col">
        <div className="px-1 py-2 flex flex-col">
          <RoutePlannerDeviceList
            organType={organType}
            selectedResourceId={selectedSecondaryOCS?.id}
            onSelect={handleOCSSelect}
            selectedHubId={selectedHub?.id}
          />
        </div>
      </div>
      <div className="gap-4 flex flex-row justify-end">
        <Button onClick={onClose} variant="outline">
          Discard
        </Button>
        <Button isDisabled={isLoading} variant="solid" color="primary" onPress={handleAddResource}>
          Add Resource
        </Button>
      </div>
    </div>
  );
};
